import axios, { AxiosAdapter } from 'axios'
import { Message, MessageBox } from 'element-ui'
import { getFilterStoreId } from '@/utils/storeContext'
import { UserModule } from '@/store/modules/user'
import {
  getRequestKey,
  pending,
  checkPending,
  removePending
} from './requestOptimize'
import router from '@/router'
import {
  handleOrderStoreMismatchResponse,
  isApiSuccess,
  isOrderStoreMismatchMsg,
  ORDER_STORE_HANDLED,
} from './orderStoreResponseHandler'
const CancelToken = axios.CancelToken;

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  'timeout': 600000
})

// Request interceptors
service.interceptors.request.use(
  (config: any) => {
    // console.log(config, 'config')
    // config.data = config.params
    // Add X-Access-Token header to every request, you can add other custom headers here
    if (UserModule.token) {
      config.headers['token'] = UserModule.token
    } else if (UserModule.token && config.url != '/login') {
      window.location.href = '/login'
      return false
    }

    // config.headers['Access-Control-Allow-Origin'] = '*'
    // config.headers['Content-Type'] = 'application/json;'
    // 在多商户模式下，为部分业务接口自动注入当前选中商户的 storeId
    const url: string = config.url || ''
    const method = (config.method || '').toLowerCase()
    const storeId = getFilterStoreId()
    if (storeId) {
      const isDishApi = url.startsWith('/dish')
      const isCategoryApi = url.startsWith('/category')
      const isOrderApi = url.startsWith('/order')
      // 列表/查询类接口通过 query 传递 storeId（已显式传入则不覆盖）
      if ((isDishApi || isCategoryApi || isOrderApi) && (method === 'get' || method === 'delete')) {
        const params = config.params || {}
        if (params.storeId == null) {
          config.params = {
            ...params,
            storeId
          }
        }
      }
      // 新增/修改类接口通过 body 传递 storeId（部分旧接口为表单参数，服务端可按需要调整）
      if ((isDishApi || isCategoryApi) && (method === 'post' || method === 'put')) {
        config.data = {
          ...(config.data || {}),
          storeId
        }
      }
      // 分类/商品启用禁用、邀请计桶、以及各业务排序（含 /sort 与 /sort/{direction}）：通过 query 传递 storeId
      if (
        method === 'post' &&
        (url.indexOf('/status/') !== -1 ||
          url.indexOf('/inviteBucket/') !== -1 ||
          url.indexOf('/sort/') !== -1 ||
          /\/sort(\?|$)/.test(url))
      ) {
        config.params = {
          ...(config.params || {}),
          storeId
        }
      }
      // 订单派送/完成：必须由页面传入订单所属 storeId
      // 数据统计报表：选店后自动带 storeId（营业额/订单/TOP10，不含全平台用户统计）
      const isReportApi =
        url.startsWith('/report/') &&
        url.indexOf('/export') === -1 &&
        url.indexOf('userStatistics') === -1
      if (isReportApi && method === 'get') {
        const hasStoreInPath = /\/report\/\w+\/\d+/.test(url)
        const params = config.params || {}
        if (!hasStoreInPath && params.storeId == null) {
          config.params = {
            ...params,
            storeId: Number(storeId),
          }
        }
      }
      // 工作台：选店后自动带 storeId
      const isWorkspaceApi = url.startsWith('/workspace/')
      if (isWorkspaceApi && method === 'get') {
        const hasStoreInPath = /\/workspace\/businessData\/\d+/.test(url)
        const params = config.params || {}
        if (!hasStoreInPath && params.storeId == null) {
          config.params = {
            ...params,
            storeId: Number(storeId),
          }
        }
      }
      const isMessageApi = url.startsWith('/messages/')
      if (isMessageApi && (method === 'get' || method === 'put')) {
        const params = config.params || {}
        if (params.storeId == null) {
          config.params = {
            ...params,
            storeId: Number(storeId),
          }
        }
      }
      // 邀请返余额 / 实惠套餐 / 派送与额度记录 / 首页轮播：按当前选中商户过滤
      const isStoreScopedOpsApi =
        url.startsWith('/invite/') ||
        url.startsWith('/waterVoucher/') ||
        url.startsWith('/record/') ||
        url.startsWith('/homeBanner') ||
        url.startsWith('/notice')
      if (isStoreScopedOpsApi && method === 'get') {
        const params = config.params || {}
        if (params.storeId == null) {
          config.params = {
            ...params,
            storeId: Number(storeId),
          }
        }
      }
    }

    // get请求映射params参数
    if (config.method === 'get' && config.params) {
      let url = (config.url || '') + '?'
      for (const propName of Object.keys(config.params)) {
        const value = config.params[propName];
        var part = encodeURIComponent(propName) + '=';
        if (value !== null && typeof (value) !== 'undefined') {
          if (typeof value === 'object') {
            for (const key of Object.keys(value)) {
              let params = propName + '[' + key + ']';
              var subPart = encodeURIComponent(params) + '=';
              url += subPart + encodeURIComponent(value[key]) + '&';
            }
          } else {
            url += part + encodeURIComponent(value) + '&';
          }
        }
      }
      url = url.slice(0, -1);
      config.params = {};
      config.url = url;
    }
    // 仅对写操作去重：GET 列表在「保存成功后立刻刷新」时极易与进行中的查询撞 key，
    // 取消/异常适配下 config.url 可能为空，旧逻辑会抛 indexOf，表现为「成功后又报错」且偶发。
    if (method !== 'get' && method !== 'head') {
      const key = getRequestKey(config)
      if (checkPending(key)) {
        const source = CancelToken.source()
        config.cancelToken = source.token
        source.cancel('重复请求')
      } else {
        pending[key] = true
      }
    }
    return config
  },
  (error: any) => {
    return Promise.reject(error)
  }
)

// Response interceptors
service.interceptors.response.use(
  async (response: any) => {
    // console.log(response, 'response')
    if (response.data.status === 401) {
      router.push('/login')
    }
    try {
      // url 在部分场景（取消/异常适配）可能为空，避免 .replace/.indexOf 抛错
      if (response.config && typeof response.config.url === 'string') {
        response.config.url = response.config.url.replace('/api', '')
      }
      const key = getRequestKey(response.config)
      removePending(key)
    } catch (_) {
      /* ignore pending cleanup errors */
    }

    const data = response.data
    if (data && !isApiSuccess(data.code) && isOrderStoreMismatchMsg(data.msg)) {
      data[ORDER_STORE_HANDLED] = true
      try {
        await handleOrderStoreMismatchResponse(response)
      } catch (_) {
        /* ignore */
      }
      return response
    }

    return response
  },
  (error: any) => {
    // console.log(error.config, pending, 'error')
    if (error && error.response) {
      switch (error.response.status) {
        case 401:
          router.push('/login')
          break;
        case 405:
          error.message = '请求错误'
      }
    }
    // 请求响应中的 config 可能不存在（如网络错误/被取消等），需要保护
    try {
      if (error && error.config) {
        if (typeof error.config.url === 'string') {
          // 请求响应中的 config 的 url 会带上代理的 api，需要去掉
          error.config.url = error.config.url.replace('/api', '')
        }
        const key = getRequestKey(error.config)
        removePending(key)
      }
    } catch (_) {
      /* ignore */
    }
    // console.log(error, pending, 'error11')
    // Message({
    //   'message': error.message,
    //   'type': 'error',
    //   'duration': 5 * 1000
    // })
    // router.push('/login')
    return Promise.reject(error)
  }
)

export default service
